import type {
	GetGrowerBenchmarkBillingSummaryQuery,
	GetGrowerBenchmarkMemberDirectoryQuery
} from "~/gql/queries/growerBenchmarkConsole.generated";
import type { GetGrowerBenchmarkRetailMonthsQuery } from "~/gql/queries/growerBenchmarkRetailMonth.generated";
import type {
	GetGrowerBenchmarkCohortPassRunsQuery,
	GetGrowerBenchmarkCohortStatisticsQuery,
	GetGrowerBenchmarkFirmMetricsQuery,
	GetGrowerBenchmarkMetricCatalogQuery,
	GetGrowerBenchmarkMyCommunitiesQuery,
	GetGrowerBenchmarkMyFirmsQuery,
	GetGrowerBenchmarkMyReviewQuery,
	GetGrowerBenchmarkReportQueueQuery,
	GetGrowerBenchmarkReviewMeteringQuery,
	GetGrowerBenchmarkReviewQuery,
	GetGrowerBenchmarkSubmissionsQuery,
	GetGrowerBenchmarkSubmissionsQueryVariables
} from "~/gql/queries/growerBenchmarks.generated";

import {
	mergeGrowerBenchmarkSubmissionPages,
	serializeGrowerBenchmarkSubmissionsQueryKey
} from "~growerBenchmarks/helpers/growerBenchmarkHomeHelpers";

export const growerBenchmarksEndpoints = {
	AcceptGrowerBenchmarkAcceptableUse: {
		invalidatesTags: ["GrowerBenchmarkCommunities"] as AddTagTypes
	},
	ApproveGrowerBenchmarkReview: {
		invalidatesTags: ["GrowerBenchmarkReviews"] as AddTagTypes
	},
	BeginGrowerBenchmarkFirmLookup: {
		invalidatesTags: ["GrowerBenchmarkAccessLog"] as AddTagTypes
	},
	GetGrowerBenchmarkBillingSummary: {
		providesTags: ["GrowerBenchmarkFirmProfiles"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkBillingSummaryQuery) => res
	},
	GetGrowerBenchmarkCohortPassRuns: {
		providesTags: ["GrowerBenchmarkMetrics"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkCohortPassRunsQuery) => res
	},
	GetGrowerBenchmarkCohortStatistics: {
		providesTags: ["GrowerBenchmarkMetrics"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkCohortStatisticsQuery) => res
	},
	GetGrowerBenchmarkFirmMetrics: {
		providesTags: ["GrowerBenchmarkMetrics"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkFirmMetricsQuery) => res
	},
	GetGrowerBenchmarkMemberDirectory: {
		providesTags: ["GrowerBenchmarkFirmProfiles"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkMemberDirectoryQuery) => res
	},
	GetGrowerBenchmarkMetricCatalog: {
		providesTags: ["GrowerBenchmarkMetrics"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkMetricCatalogQuery) => res
	},
	GetGrowerBenchmarkMyCommunities: {
		providesTags: ["GrowerBenchmarkCommunities"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkMyCommunitiesQuery) => res
	},
	GetGrowerBenchmarkMyFirms: {
		providesTags: ["GrowerBenchmarkFirms"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkMyFirmsQuery) => res
	},
	GetGrowerBenchmarkMyReview: {
		providesTags: ["GrowerBenchmarkReviews"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkMyReviewQuery) => res
	},
	GetGrowerBenchmarkReportQueue: {
		providesTags: ["GrowerBenchmarkReviews"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkReportQueueQuery) => res
	},
	GetGrowerBenchmarkRetailMonths: {
		providesTags: ["GrowerBenchmarkRetailMonths"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkRetailMonthsQuery) => res
	},
	GetGrowerBenchmarkReview: {
		providesTags: ["GrowerBenchmarkReviews"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkReviewQuery) => res
	},
	GetGrowerBenchmarkReviewMetering: {
		providesTags: ["GrowerBenchmarkReviews"] as AddTagTypes,
		transformResponse: (res: GetGrowerBenchmarkReviewMeteringQuery) => res
	},
	GetGrowerBenchmarkSubmissions: {
		forceRefetch({
			currentArg,
			previousArg
		}: {
			currentArg: GetGrowerBenchmarkSubmissionsQueryVariables | undefined;
			previousArg: GetGrowerBenchmarkSubmissionsQueryVariables | undefined;
		}) {
			return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
		},
		merge(
			currentCache: GetGrowerBenchmarkSubmissionsQuery,
			newData: GetGrowerBenchmarkSubmissionsQuery,
			{ arg }: { arg: GetGrowerBenchmarkSubmissionsQueryVariables }
		): GetGrowerBenchmarkSubmissionsQuery {
			if (!newData) return currentCache;
			return {
				...newData,
				growerBenchmarkSubmissions: mergeGrowerBenchmarkSubmissionPages(
					currentCache?.growerBenchmarkSubmissions ?? [],
					newData.growerBenchmarkSubmissions,
					!arg.after
				)
			};
		},
		providesTags: ["GrowerBenchmarkSubmissions"] as AddTagTypes,
		serializeQueryArgs: ({
			endpointName,
			queryArgs
		}: {
			endpointName: string;
			queryArgs: GetGrowerBenchmarkSubmissionsQueryVariables;
		}) => serializeGrowerBenchmarkSubmissionsQueryKey(endpointName, queryArgs),
		transformResponse: (res: GetGrowerBenchmarkSubmissionsQuery) => res
	},
	GrowerBenchmarkUpsertMetric: {
		invalidatesTags: ["GrowerBenchmarkMetrics"] as AddTagTypes
	},
	OpenGrowerBenchmarkReviewForEdit: {
		invalidatesTags: ["GrowerBenchmarkReviews"] as AddTagTypes
	},
	ReleaseGrowerBenchmarkReview: {
		invalidatesTags: ["GrowerBenchmarkFirms", "GrowerBenchmarkReviews"] as AddTagTypes
	},
	RestoreGrowerBenchmarkReviewVersion: {
		invalidatesTags: ["GrowerBenchmarkReviews"] as AddTagTypes
	},
	RevokeGrowerBenchmarkRosterEntry: {
		invalidatesTags: ["GrowerBenchmarkAccessLog", "GrowerBenchmarkCommunities"] as AddTagTypes
	},
	SaveGrowerBenchmarkReviewVersion: {
		invalidatesTags: ["GrowerBenchmarkReviews"] as AddTagTypes
	},
	SetGrowerBenchmarkFirmChecklistItem: {
		invalidatesTags: ["GrowerBenchmarkFirmProfiles"] as AddTagTypes
	},
	SetGrowerBenchmarkFirmProfile: {
		invalidatesTags: ["GrowerBenchmarkCommunities", "GrowerBenchmarkFirmProfiles"] as AddTagTypes
	},
	SetGrowerBenchmarkRetailMonthValues: {
		invalidatesTags: ["GrowerBenchmarkRetailMonths", "GrowerBenchmarkSubmissions"] as AddTagTypes
	},
	SetGrowerBenchmarkRosterEntry: {
		invalidatesTags: ["GrowerBenchmarkAccessLog", "GrowerBenchmarkCommunities"] as AddTagTypes
	}
};

export default growerBenchmarksEndpoints;
